import config from 'Cloud/config';
import classNames from 'clsx';
import React, { type ReactElement, memo, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { isTabFolderSharingDesign } from 'reactApp/appHelpers/featuresHelpers/features/tabsFolderSharingDesign';
import { SharingContentBiz } from 'reactApp/components/SharingNewBiz/SharingContentBiz';
import { HOT_KEY_SCOPE_NAME } from 'reactApp/components/SharingWindow/Sharing.constants';
import type { SharingContentProps } from 'reactApp/components/SharingWindow/Sharing.types';
import { SharingContent } from 'reactApp/components/SharingWindow/SharingContent/SharingContent';
import { SharingContentTabs } from 'reactApp/components/SharingWindow/SharingContentTabs/SharingContentTabs';
import { SHARING_VARIANT } from 'reactApp/components/SharingWindow/SharingNew/Weblink/WeblinkList/SharingNewWeblinkList.types';
import { SharingOnboarding } from 'reactApp/components/SharingWindow/SharingOnboarding/SharingOnboarding';
import { SharingContentRedesign } from 'reactApp/components/SharingWindow/SharingRedisign/SharingContentRedesign/SharingContentRedesign';
import { useHotKeyScope } from 'reactApp/hooks/useHotkeyScope';
import { useIsWindowIntegrationSmall } from 'reactApp/hooks/useIsWindowIntegrationSmall';
import { isAlbum } from 'reactApp/modules/albums/albums.types';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import type { IPropsWithPopup } from 'reactApp/modules/popup/popup.helpers';
import { isOwnPublic as isOwnPublicSelector } from 'reactApp/modules/public/public.selectors';
import { getItemById, getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import Content from 'reactApp/ui/Content/Content';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { sendXray } from 'reactApp/utils/ga';

import { useOnboardingEnabled } from './hooks/onboardingEnabled.hook';
import styles from './SharingNew.css';

const SHARING_CONTENT = {
    [SHARING_VARIANT.tabs]: {
        SharingContentComponent: SharingContentTabs,
        sharingVariant: SHARING_VARIANT.tabs,
    },
    [SHARING_VARIANT.redesign]: {
        SharingContentComponent: SharingContentRedesign,
        sharingVariant: SHARING_VARIANT.redesign,
    },
    [SHARING_VARIANT.biz]: {
        SharingContentComponent: SharingContentBiz,
        sharingVariant: SHARING_VARIANT.biz,
    },
    [SHARING_VARIANT.default]: {
        SharingContentComponent: SharingContent,
        sharingVariant: SHARING_VARIANT.default,
    },
};

export const SharingNewDesktop = memo(function SharingNewDesktop(props: IPropsWithPopup<SharingContentProps>): ReactElement {
    const { itemStorage, id, from, isPhone } = props;

    const isWindowSmall = useIsWindowIntegrationSmall();
    const isPublic = !!config.get('PUBLIC');

    const item = useSelector((state: RootState) => {
        return itemStorage ? getStorageItemById(state, itemStorage, id) : getItemById(state, id);
    });
    const isBiz = useSelector(UserSelectors.isBizUser);
    const isOwnPublic = useSelector((state: RootState) => isPublic && isOwnPublicSelector(state));
    const isAlbumItem = isAlbum(item);
    const isFolder = item?.isFolder;

    const { SharingContentComponent, sharingVariant } = useMemo(() => {
        if (!item || isAlbumItem) {
            return SHARING_CONTENT[SHARING_VARIANT.default];
        } else if (isBiz && isFolder) {
            // Для папок б2б юзерам пока остается старый шаринг
            return SHARING_CONTENT[SHARING_VARIANT.biz];
        } else if (isFolder && isTabFolderSharingDesign) {
            return SHARING_CONTENT[SHARING_VARIANT.tabs];
        } else if (!isFolder && (!isPublic || isOwnPublic)) {
            return SHARING_CONTENT[SHARING_VARIANT.redesign];
        }

        return SHARING_CONTENT[SHARING_VARIANT.default];
    }, [isAlbumItem, isBiz, isFolder, isOwnPublic, isPublic, item]);

    const { onboardingFreeUsers } = useOnboardingEnabled(true);

    useEffect(() => {
        let type = 'file';
        const userType = isBiz ? 'biz' : 'b2c';

        if (isAlbumItem) {
            type = 'album';
        } else if (isFolder) {
            type = 'folder';
        }

        sendXray(['sharing-modal-desktop-mount', type, userType]);
    }, []);

    return (
        <Dialog
            open
            id="sharing-new"
            onCancel={props.onClose}
            className={classNames(styles.modal, {
                [styles.tutoria]: isWindowSmall,
                [styles.sharingTabs]: sharingVariant === SHARING_VARIANT.tabs,
                [styles.sharingRedesign]: sharingVariant === SHARING_VARIANT.redesign,
            })}
            bottomContent={
                onboardingFreeUsers && isFolder ? (
                    <SharingOnboarding
                        isPhone={isPhone}
                        from={from}
                        id={id}
                        itemStorage={itemStorage}
                        isTabDesign={sharingVariant === SHARING_VARIANT.tabs}
                    />
                ) : null
            }
        >
            <Content isModal>
                <SharingContentComponent onboardingEnabled {...props} />
            </Content>
        </Dialog>
    );
});

export const SharingNewPhone = memo(function SharingNewPhone(props: IPropsWithPopup<SharingContentProps>): ReactElement {
    const scrollRef = useRef(null);

    return (
        <MobileDialog id="sharing-new" closeOnDimmerClick mod="base" topmost onClose={props.onClose} scrollRef={scrollRef}>
            <SharingContent {...props} isPhone ref={scrollRef} />
        </MobileDialog>
    );
});

export const SharingNew = memo(function SharingNew(props: IPropsWithPopup<SharingContentProps>): ReactElement {
    const isPhone = useSelector(EnvironmentSelectors.isPhone);
    useHotKeyScope(HOT_KEY_SCOPE_NAME);

    return isPhone ? <SharingNewPhone {...props} /> : <SharingNewDesktop {...props} />;
});
